import { useEffect, useState } from 'react';
import type { MapRef } from 'react-map-gl';

/**
 * Whether the map is zoomed out past `threshold`.
 *
 * A boolean rather than the zoom itself, which changes every animation frame of
 * every gesture and would re-render the map on each one.
 *
 * @param {MapRef | null} mapRef - Handle on the map, once it has loaded.
 * @param {number} threshold - Zoom to compare against.
 * @return {boolean} True while the map is below the threshold; false before the
 * map exists, so nothing is claimed about a map that isn't there yet.
 */
export const useMapZoomBelow = (
  mapRef: MapRef | null,
  threshold: number
): boolean => {
  const [below, setBelow] = useState(false);

  useEffect(() => {
    if (!mapRef) {
      return undefined;
    }

    const map = mapRef.getMap();
    const read = () => setBelow(map.getZoom() < threshold);

    read();
    map.on('zoom', read);

    return () => {
      map.off('zoom', read);
    };
  }, [mapRef, threshold]);

  return below;
};
